/* eslint-disable consistent-return */
import React, {FC, useEffect, useMemo, useState, useRef} from 'react';
import {AdSlot, DFPManager, Size, SizeMapping} from 'react-dfp';
import {createPortal} from 'react-dom';
import {adUnitToDivId} from 'utils/stringTools';

import './styles.scss';

const isViewable = (id: string) => {
  const element = document.getElementById(id);
  if (!element) return false;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const box = element.getBoundingClientRect();

  /* eslint-disable no-nested-ternary */
  const boxTop = box.top > 0 ? (box.top < viewportHeight ? box.top : viewportHeight) : 0;
  const boxBottom = box.bottom > 0 ? (box.bottom < viewportHeight ? box.bottom : viewportHeight) : 0;
  const boxLeft = box.left > 0 ? (box.left < viewportWidth ? box.left : viewportWidth) : 0;
  const boxRight = box.right > 0 ? (box.right < viewportWidth ? box.right : viewportWidth) : 0;

  const viewableArea = (boxRight - boxLeft) * (boxBottom - boxTop);
  const totalArea = box.width * box.height;

  const isTabActive = !document.hidden;

  // console.log('id', id,'totalArea:', totalArea, 'viewableArea:', viewableArea, 'percent:', (viewableArea / totalArea), 'isViewable:', (isTabActive && viewableArea / totalArea >= 0.05));

  return (isTabActive && viewableArea / totalArea >= 0.5);
}; // end of isViewable function

interface Props {
  adUnit: string;
  divId?: string;
  scope: string;
  sizes: Size[];
  sizeMapping?: SizeMapping[];
  stuck?: boolean;
  sticky?: boolean;
  autoRefresh?: boolean;
  root?: HTMLElement | null;
}

const Ad: FC<Props> = ({
  adUnit,
  divId,
  scope,
  sizes,
  sizeMapping,
  stuck,
  sticky,
  autoRefresh,
  root,
}: Props) => {
  const slotId = divId || adUnitToDivId(adUnit, scope);
  const [isFilled, setIsFilled] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const premiumRoot = useRef<HTMLElement | null>(null);

  // refresh responsive ads on resize
  useEffect(() => {
    if (sizeMapping) {
      const breakpoints = sizeMapping
        .map(m => m.viewport[0])
        .filter(w => w > 0)
        .sort()
        .reverse();

      let timeoutId: number;

      const resizeListener = () => {
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
          // loops array until true is returned
          breakpoints.some(breakpoint => {
            // crossed breakpoint from bigger to smaller OR from smaller to bigger
            if (
              (viewportWidth > breakpoint && window.innerWidth <= breakpoint) ||
              (viewportWidth < breakpoint && window.innerWidth >= breakpoint)
            ) {
              DFPManager.refresh(slotId);
              return true;
            }
            return false;
          });
          setViewportWidth(window.innerWidth);
        }, 500);
      };

      window.addEventListener('resize', resizeListener);

      return () => {
        window.removeEventListener('resize', resizeListener);
      };
    }
  }, []);

  // auto refresh
  useEffect(() => {
      const interval = setInterval(() => {
        if (autoRefresh) {
            const isViewableVar = isViewable(slotId);
            // console.log('isViewableVar', isViewableVar, 'check:', isViewableVar === true, 'isViewable fn:', isViewable(slotId));
            if (isViewableVar === true) {
              // console.log(`%cisViewable > refresh > ${slotId}`, "background-color:yellow; color: black; padding:2px 4px;");
              DFPManager.refresh(slotId);
            }
        }
      }, 32000);

    return () => {
      if (interval) {
        // console.log('clear interval')
        clearInterval(interval);
      }
    };
  }, [slotId, autoRefresh]);

  // cleanup
  useEffect(
      () => () => {
        /* Note: is temp fix for task ADOPS-132.
            window.innerWidth > 998 fixes problem in mobile
            when navigating from a tab to tab LIVE MATCH.
            */
        if (window.innerWidth > 998) {
          DFPManager.destroyGPTSlots();
        }
        // console.log(`%c destroyGPTSlots ${slotId}`, "background-color:red; color: white; padding:2px 4px;");
        // console.log(`%c scope ${scope}`, "background-color:green; color: white; padding:2px 4px;");
      },
      [scope]
  );

  const dom = useMemo(
    () => (
      <div
        className={`Ad ${sticky ? 'Ad--sticky' : ''} ${stuck ? 'Ad--stuck' : ''} ${
          isFilled ? 'Ad--filled' : ''}`}
      >
        <AdSlot
          adUnit={adUnit}
          slotId={slotId}
          sizes={sizes}
          sizeMapping={sizeMapping}
          onSlotRender={data => {
            if (!data.event.isEmpty) {
              setIsFilled(true);
              const premiumSlotId = data.event.slot.getSlotElementId();
              if(premiumSlotId === "matchcenter-hp-billboard-matchcenterpage" || premiumSlotId === "matchcenter-ros-billboard-matchcenterpage"){
                premiumRoot.current = document.getElementById("matchcenter-premium-root");
                if(premiumRoot.current?.children[0].className.includes("Ad--filled")){
                  premiumRoot.current?.classList.add("adSlot-height--premium"); 
                } 
              }
            } else {
                setIsFilled(false);
              }
          }}
        />
      </div>
    ),
    [adUnit, isFilled, sizeMapping, sizes, slotId, sticky, stuck]
  );

  // if portal is used but non-existent, don't run element;
  if (root === null) return null;
  if (root === undefined) return dom;
  return root && createPortal(dom, root);
};

export default React.memo(Ad);
